import Vue from 'vue'
import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import {IShortChannel, IShortServiceProvider} from "@/model/shortInterfaces";
import responseHandler from "@/plugins/errorHandling";
import {IServiceProvider} from "@/model/Service/serviceProviderInterfaces";
import {IServiceProviderLogInterface} from "@/model/Service/IServiceProviderLogInterface";
import {IServiceTag} from "@/model/Service/serviceInterfaces";

export interface ITypeExtensionMap {
  type: string;
  extensions: string[];
}

export interface ICategoryServiceProvider {
  id: number;
  category_id: number;
  service_provider_id: number;
  sp_position: number;
  added_by_user_id: number;
  service_provider_name: string;
}

export interface ICommunityOption {
  id: number;
  name: string;
}

export interface ICity {
  id: number;
  name: string;
  country_id: number;
}

interface ServiceProviderState {
  categoryServiceProviders: ICategoryServiceProvider[];
  serviceProvider?: IServiceProvider;
  serviceProviders: IShortServiceProvider[];
  serviceProviderList: IShortServiceProvider[];
  serviceProviderListCurrentPage: number;
  serviceProviderListLastPage: number;
  serviceProviderListTotal: number;
  serviceProviderListPerPage: number;
  typeExtensionMaps: ITypeExtensionMap[];
  serviceProviderLogs: IServiceProviderLogInterface[];
  communities: ICommunityOption[];
  cities: ICity[];
  tags: IServiceTag[];
  // these are the channels available to assign the current serviceProvider
  serviceProvidersChannels: IShortChannel[];
  errors: IErrors;
}

const state: ServiceProviderState = {
  categoryServiceProviders: [],
  serviceProvider: undefined,
  serviceProviders: [],
  serviceProviderList: [],
  serviceProviderListCurrentPage: 1,
  serviceProviderListLastPage: 1,
  serviceProviderListTotal: 0,
  serviceProviderListPerPage: 20,
  typeExtensionMaps: [],
  serviceProviderLogs: [],
  communities: [],
  cities: [],
  serviceProvidersChannels: [],
  tags: [],
  errors: {},
};

const getters: GetterTree<ServiceProviderState, RootState> = {
  categoryServiceProviders: (state) => state.categoryServiceProviders,
  serviceProvider: (state) => state.serviceProvider,
  serviceProviders: (state) => state.serviceProviders,
  serviceProviderList: (state) => state.serviceProviderList,
  serviceProviderListCurrentPage: (state) => state.serviceProviderListCurrentPage,
  serviceProviderListLastPage: (state) => state.serviceProviderListLastPage,
  serviceProviderListTotal: (state) => state.serviceProviderListTotal,
  serviceProviderListPerPage: (state) => state.serviceProviderListPerPage,
  typeExtensionMaps: (state) => state.typeExtensionMaps,
  serviceProviderLogs: (state) => state.serviceProviderLogs,
  communities: (state) => state.communities,
  cities: (state) => state.cities,
  tags: (state) => state.tags,
  serviceProvidersChannels: (state) => state.serviceProvidersChannels,
  errors: (state) => state.errors,
  serviceProvidersByIds: (state) => (ids: number[]) => state.serviceProviders.filter(sp => ids.includes(sp.id)),
  serviceProviderById: (state) => (id: number) =>  state.serviceProviders.find(sp => sp.id === id),

};

const actions: ActionTree<ServiceProviderState, RootState> = {
  fetchServiceProvider: ({ commit }, spId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + spId,
      method: "GET",
    }),
    commit,
    'serviceProviderLoaded'
  ),
  forceFetchServiceProviders: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/short',
      method: 'GET',
    }),
    commit,
    'serviceProvidersLoaded'
  ),
  fetchServiceProvidersList: ({commit}, filters): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/list',
      method: 'GET',
      params: filters,
    }),
    commit,
    'serviceProviderListLoaded'
  ),
  setDemo: ({commit}, {serviceProviderId, isDemo}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + serviceProviderId + '/demo',
      method: 'PUT',
      data: '' + isDemo,
    }),
    commit,
    'serviceProviderListItemUpdated'
  ),
  fetchServiceProviders: ({commit, state}): any =>
    responseHandler.debounce<IShortServiceProvider[]>(
    state.serviceProviders,
    30,
    () => axios({
      url: settings.endpoint + 'serviceProviders/short',
      method: 'GET',
    }),
    commit,
    'serviceProvidersLoaded'
  ),
  fetchTypeExtensionMaps: ({commit}): any => responseHandler.debounce<ITypeExtensionMap[]>(
    state.typeExtensionMaps,
    30,
    () => axios({
      url: settings.endpoint + 'serviceProviders/typeExtensionMaps',
      method: 'GET',
    }),
    commit,
    'typeExtensionMapsLoaded'
  ),
  fetchCities: ({commit, state}): any => responseHandler.debounce<ICity[]>(
    state.cities,
    30,
    () => axios({
      url: settings.endpoint + 'serviceProviders/cities',
      method: 'GET',
    }),
    commit,
    'citiesLoaded'
  ),
  fetchCommunities: ({commit, state}): any => responseHandler.debounce<ICommunityOption[]>(
    state.communities,
    30,
    () => axios({
      url: settings.endpoint + 'serviceProviders/communities',
      method: 'GET',
    }),
    commit,
    'communitiesLoaded'
  ),
  fetchTags: ({ commit }): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/serviceProviderTags',
      method: "GET",
    }),
    commit,
    'tagsLoaded'
  ),
  fetchServiceProviderLogs: ({commit}): any => responseHandler.handle(
    () => axios({
        url: settings.endpoint + 'serviceProviders/logs',
        method: 'GET',
    }),
    commit,
    'serviceProviderLogsLoaded'
  ),
  // @todo should we invalidate debounce timeout once a provider is updated?
  updateServiceProvider: ({commit}, {serviceProviderId, data}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + serviceProviderId,
      method: 'PATCH',
      data,
    }),
    commit,
    'serviceProviderLoaded'
  ),
  fetchServiceProvidersChannels: ({commit}, spId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + spId + '/channels',
      method: 'GET',
    }),
    commit,
    'serviceProvidersChannelsLoaded'
  ),
  duplicateServiceProvider: ({commit}, spId) => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + spId + '/duplicate',
      method: 'POST',
    }),
    commit,
    'serviceProviderDuplicated'
  ),
  createServiceProvider: ({commit}, data): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders',
      method: 'POST',
      data,
    }),
    commit,
    'serviceProviderCreated'
  ),
  deleteServiceProvider: ({commit}, spId) => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + spId,
      method: 'DELETE',
    }),
    commit,
    'serviceProviderDeleted'
  ),
  connectChannel: ({ commit }, {serviceProviderId, channelId, connect}): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'serviceProviders/' + serviceProviderId + '/channels/' + channelId,
        method: connect ? 'PUT' : 'DELETE',
      }),
      commit,
      'serviceProviderLoaded'
    )
  },
  connectCategory: ({ commit }, {serviceProviderId, categoryId, connect}): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'serviceProviders/' + serviceProviderId + '/categories/' + categoryId,
        method: connect ? 'PUT' : 'DELETE',
      }),
      commit,
      'serviceProviderLoaded'
    )
  },
  fetchCategoryServiceProviders: ({ commit }, categoryId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/categories/' + categoryId,
      method: "GET",
    }),
    commit,
    'categoryServiceProvidersLoaded'
  ),
  moveUp: ({ commit }, {categoryId, serviceProviderId}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + serviceProviderId + '/categories/' + categoryId + '/up',
      method: 'POST',
    }),
    commit,
    'categoryServiceProvidersLoaded'
  ),
  moveDown: ({ commit }, {categoryId, serviceProviderId}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + serviceProviderId + '/categories/' + categoryId + '/down',
      method: 'POST',
    }),
    commit,
    'categoryServiceProvidersLoaded'
  ),
  setActive: ({commit}, {serviceProviderId, active}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + serviceProviderId + '/active/',
      method: 'PUT',
      data: '' + active,
    }),
    commit,
    'listServiceProviderLoaded'
  ),
  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  },
  cleanError: ({commit}, index: any): any => {
    commit('cleanError', index);
  },
};

const mutations: MutationTree<ServiceProviderState> = {
  categoryServiceProvidersLoaded(state, payload) {
    state.categoryServiceProviders = payload;
  },
  serviceProviderLoaded(state, payload) {
    state.serviceProvider = payload;
  },
  listServiceProviderLoaded(state, payload) {
    state.serviceProviders.forEach(each => {
      if (each.id == payload.id) {
        Object.assign(each, payload);
      }
    });
  },
  tagsLoaded(state, payload) {
    state.tags = payload;
  },
  serviceProvidersLoaded(state, payload) {
    state.serviceProviders = payload;
  },
  typeExtensionMapsLoaded(state, payload) {
    state.typeExtensionMaps = payload;
  },
  communitiesLoaded(state, payload) {
    state.communities = payload;
  },
  citiesLoaded(state, payload) {
    state.cities = payload;
  },
  serviceProviderLogsLoaded(state, payload){
    state.serviceProviderLogs = payload;
  },
  serviceProvidersChannelsLoaded(state, payload) {
    state.serviceProvidersChannels = payload;
  },
  serviceProviderListLoaded(state, payload: any) {
    if (!payload) {
      state.serviceProviderList = [];
      return;
    }
    state.serviceProviderList = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    state.serviceProviderListCurrentPage = payload.current_page || 1;
    state.serviceProviderListLastPage = payload.last_page || 1;
    state.serviceProviderListTotal = payload.total || 0;
    state.serviceProviderListPerPage = payload.per_page || 20;
  },
  serviceProviderListItemUpdated(state, payload: IShortServiceProvider) {
    if (!payload) return;
    state.serviceProviderList = state.serviceProviderList.map(function(each) {
      if (each.id === payload.id) {
        return Object.assign({}, each, payload);
      }
      return each;
    });
  },
  serviceProviderCreated() {}, // handled by navigation in the component
  serviceProviderDuplicated() {}, // no action needed, but we need the function to prevent error throw
  serviceProviderDeleted() {}, // handled by navigation in the component
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
  cleanError(state, index: string) {
    Vue.delete(state.errors, index);
  },
};

const namespaced: boolean = true;

export const serviceProviderModule: Module<ServiceProviderState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
